// ---------------------
// Custom Dropdown
// Example Usage:

// <custom-dropdown position="right">
//   <span slot="label">Select an option</span>
//   <div slot="content">
//     <ul>
//       <li>item 1</li>
//       <li>item 2</li>
//       <li>item 3</li>
//     </ul>
//   </div>
// </custom-dropdown>



class CustomDropdown extends HTMLElement {
  static get observedAttributes() {
    return ["position", "trigger", "hide-arrow"];
  }

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });

    // Create template
    const template = document.createElement("template");
    template.innerHTML = `
        <style>
          .container {
            position: relative;
            display: inline-block;
          }

          .label-container {
            display: flex;
            align-items: center;
            cursor: pointer;
          }

          .arrow {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .dropdown-content {
            position: absolute;
            z-index: 999;
            width: max-content;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            overflow: hidden;
            opacity: 0;
            transition: 
              opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            visibility: hidden;
          }

          /* Position variations */
          .dropdown-content[data-position="bottom"] {
            top: calc(100% + 1rem);
            left: 50%;
            transform: translateY(10px) translateX(-50%);
          }

          .dropdown-content[data-position="bottom-start"] {
            top: calc(100% + 1rem);
            left: 0;
            transform: translateY(10px);
          }

          .dropdown-content[data-position="bottom-end"] {
            top: calc(100% + 1rem);
            right: 0;
            transform: translateY(10px);
          }
          
          .dropdown-content[data-position="bottom-left"] {
            top: calc(100% + 1rem);
            left: 0;
            transform: translateY(10px);
          }

          .dropdown-content[data-position="bottom-right"] {
            top: calc(100% + 1rem);
            right: 0;
            transform: translateY(10px);
          }

          .dropdown-content[data-position="top"] {
            bottom: calc(100% + 1rem);
            left: 50%;
            transform: translateY(-10px) translateX(-50%);
          }

          .dropdown-content[data-position="top-start"] {
            bottom: calc(100% + 1rem);
            left: 0;
            transform: translateY(-10px);
          }

          .dropdown-content[data-position="top-end"] {
            bottom: calc(100% + 1rem);
            right: 0;
            transform: translateY(-10px);
          }
          
          .dropdown-content[data-position="top-left"] {
            bottom: calc(100% + 1rem);
            left: 0;
            transform: translateY(-10px);
          }

          .dropdown-content[data-position="top-right"] {
            bottom: calc(100% + 1rem);
            right: 0;
            transform: translateY(-10px);
          }

          .dropdown-content[data-position="left"] {
            top: 50%;
            right: calc(100% + 1rem);
            transform: translateY(-50%) translateX(-10px);
          }
          
          .dropdown-content[data-position="left-top"] {
            top: 0;
            right: calc(100% + 1rem);
            transform: translateX(-10px);
          }
          
          .dropdown-content[data-position="left-bottom"] {
            bottom: 0;
            right: calc(100% + 1rem);
            transform: translateX(-10px);
          }

          .dropdown-content[data-position="right"] {
            top: 50%;
            left: calc(100% + 1rem);
            transform: translateY(-50%) translateX(10px);
          }
          
          .dropdown-content[data-position="right-top"] {
            top: 0;
            left: calc(100% + 1rem);
            transform: translateX(10px);
          }
          
          .dropdown-content[data-position="right-bottom"] {
            bottom: 0;
            left: calc(100% + 1rem);
            transform: translateX(10px);
          }

          /* Modified hover states to use a class instead */
          .container.active .dropdown-content[data-position="bottom"],
          .container.active .dropdown-content[data-position="bottom-start"],
          .container.active .dropdown-content[data-position="bottom-end"],
          .container.active .dropdown-content[data-position="bottom-left"],
          .container.active .dropdown-content[data-position="bottom-right"] {
            opacity: 1;
            visibility: visible;
          }
          
          .container.active .dropdown-content[data-position="bottom"] {
            transform: translateY(0) translateX(-50%);
          }

          .container.active .dropdown-content[data-position="bottom-start"],
          .container.active .dropdown-content[data-position="bottom-left"] {
            transform: translateY(0);
          }

          .container.active .dropdown-content[data-position="bottom-end"],
          .container.active .dropdown-content[data-position="bottom-right"] {
            transform: translateY(0);
          }

          .container.active .dropdown-content[data-position="top"],
          .container.active .dropdown-content[data-position="top-start"],
          .container.active .dropdown-content[data-position="top-end"],
          .container.active .dropdown-content[data-position="top-left"],
          .container.active .dropdown-content[data-position="top-right"] {
            opacity: 1;
            visibility: visible;
          }
          
          .container.active .dropdown-content[data-position="top"] {
            transform: translateY(0) translateX(-50%);
          }

          .container.active .dropdown-content[data-position="top-start"],
          .container.active .dropdown-content[data-position="top-left"] {
            transform: translateY(0);
          }

          .container.active .dropdown-content[data-position="top-end"],
          .container.active .dropdown-content[data-position="top-right"] {
            transform: translateY(0);
          }

          .container.active .dropdown-content[data-position="left"],
          .container.active .dropdown-content[data-position="left-top"],
          .container.active .dropdown-content[data-position="left-bottom"] {
            opacity: 1;
            visibility: visible;
          }
          
          .container.active .dropdown-content[data-position="left"] {
            transform: translateY(-50%) translateX(0);
          }
          
          .container.active .dropdown-content[data-position="left-top"],
          .container.active .dropdown-content[data-position="left-bottom"] {
            transform: translateX(0);
          }

          .container.active .dropdown-content[data-position="right"],
          .container.active .dropdown-content[data-position="right-top"],
          .container.active .dropdown-content[data-position="right-bottom"] {
            opacity: 1;
            visibility: visible;
          }
          
          .container.active .dropdown-content[data-position="right"] {
            transform: translateY(-50%) translateX(0);
          }
          
          .container.active .dropdown-content[data-position="right-top"],
          .container.active .dropdown-content[data-position="right-bottom"] {
            transform: translateX(0);
          }

          .container.active .arrow {
            transform: rotate(180deg);
          }

          /* Replace the hover-specific styles with position-aware ones */
          .container[data-trigger="hover"]:hover .dropdown-content {
            opacity: 1;
            visibility: visible;
          }
          
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom"] {
            transform: translateY(0) translateX(-50%);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-start"],
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-left"] {
            transform: translateY(0);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-end"],
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-right"] {
            transform: translateY(0);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="top"] {
            transform: translateY(0) translateX(-50%);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-start"],
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-left"] {
            transform: translateY(0);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-end"],
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-right"] {
            transform: translateY(0);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="left"] {
            transform: translateY(-50%) translateX(0);
          }
          
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="left-top"],
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="left-bottom"] {
            transform: translateX(0);
          }

          .container[data-trigger="hover"]:hover .dropdown-content[data-position="right"] {
            transform: translateY(-50%) translateX(0);
          }
          
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="right-top"],
          .container[data-trigger="hover"]:hover .dropdown-content[data-position="right-bottom"] {
            transform: translateX(0);
          }

          .container[data-trigger="hover"]:hover .arrow {
            transform: rotate(180deg);
          }

          /* Buffer zones for different positions */
          .container::before {
            content: '';
            position: absolute;
            background: transparent;
          }

          /* Bottom positions buffer */
          .container:has(.dropdown-content[data-position^="bottom"])::before {
            top: 100%;
            left: 0;
            right: 0;
            height: 1rem;
          }

          /* Top positions buffer */
          .container:has(.dropdown-content[data-position^="top"])::before {
            bottom: 100%;
            left: 0;
            right: 0;
            height: 1rem;
          }

          /* Left position buffer */
          .container:has(.dropdown-content[data-position^="left"])::before {
            top: 0;
            bottom: 0;
            right: 100%;
            width: 1rem;
          }

          /* Right position buffer */
          .container:has(.dropdown-content[data-position^="right"])::before {
            top: 0;
            bottom: 0;
            left: 100%;
            width: 1rem;
          }
          
          /* Hide arrow when attribute is set */
          .arrow.hidden {
            display: none;
          }
        </style>

        <div class="container">
          <div class="label-container">
            <slot name="label"></slot>
            <svg class="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
          <div class="dropdown-content">
            <slot name="content"></slot>
          </div>
        </div>
      `;

    shadow.appendChild(template.content.cloneNode(true));
    this.dropdownContent = shadow.querySelector(".dropdown-content");
    this.container = shadow.querySelector(".container");
    this.labelContainer = shadow.querySelector(".label-container");
    this.arrow = shadow.querySelector(".arrow");
  }

  connectedCallback() {
    // Set default position if none provided
    if (!this.hasAttribute("position")) {
      this.setAttribute("position", "bottom");
    }
    // Set default trigger if none provided
    if (!this.hasAttribute("trigger")) {
      this.setAttribute("trigger", "hover");
    }

    this.updatePosition();
    this.setupTrigger();
    this.updateArrowVisibility();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "position" && oldValue !== newValue) {
      this.updatePosition();
    }
    if (name === "trigger" && oldValue !== newValue) {
      this.setupTrigger();
    }
    if (name === "hide-arrow" && oldValue !== newValue) {
      this.updateArrowVisibility();
    }
  }

  updatePosition() {
    const position = this.getAttribute("position") || "bottom";
    this.dropdownContent.setAttribute("data-position", position);
  }

  updateArrowVisibility() {
    if (this.hasAttribute("hide-arrow")) {
      this.arrow.classList.add("hidden");
    } else {
      this.arrow.classList.remove("hidden");
    }
  }

  setupTrigger() {
    const trigger = this.getAttribute("trigger") || "hover";
    this.container.setAttribute("data-trigger", trigger);

    // Remove existing listeners
    this.labelContainer.removeEventListener("click", this.toggleDropdown);

    // Setup click handler if needed
    if (trigger === "click") {
      this.toggleDropdown = () => {
        this.container.classList.toggle("active");
      };

      this.labelContainer.addEventListener("click", this.toggleDropdown);

      // Close dropdown when clicking outside
      document.addEventListener("click", (e) => {
        if (!this.contains(e.target)) {
          this.container.classList.remove("active");
        }
      });
    }
  }
}


if (!customElements.get("custom-dropdown")) {
  customElements.define("custom-dropdown", CustomDropdown);
}






// 
// =================================================================================






/**
 * SidebarDrawer - A custom web component for creating sidebar drawers
 * 
 * Usage:
 * <sidebar-drawer id="cart-drawer" position="right" bg-color="#f5f5f5">
 *   <div slot="header">Cart</div> <!-- Optional header content -->
 *   <div>Content goes here</div> <!-- No slot keyword needed for body -->
 *   <div slot="footer">Footer content</div> <!-- Optional footer content -->
 * </sidebar-drawer>
 * 
 * <!-- Open the drawer -->
 * <button onclick="document.getElementById('cart-drawer').open()">Open Drawer</button>
 * 
 * Attributes:
 * - position: "left", "right", "top", "bottom" (default: "right")
 * - width: CSS width value (default: "20rem")
 * - bg-color: CSS color value (default: "#fff")
 * - show-overlay: "true" or "false" (default: "true")
 * - lock-body-scroll: "true" or "false" (default: "true")
 * - no-body-padding: boolean attribute to remove body padding
 */

class SidebarDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Create a shadow DOM
    this.attachShadow({ mode: 'open' });
    
    // Define drawer properties with default values
    this.position = this.getAttribute('position') || 'right';
    this.drawerWidth = this.getAttribute('width') || '20rem';
    this.showOverlay = this.hasAttribute('show-overlay') ? this.getAttribute('show-overlay') !== 'false' : true;
    this.lockBodyScroll = this.hasAttribute('lock-body-scroll') ? this.getAttribute('lock-body-scroll') !== 'false' : true;
    this.noBodyPadding = this.hasAttribute('no-body-padding');
    this.backgroundColor = this.getAttribute('bg-color') || '#fff';
    
    // Initialize the drawer
    this._createDrawer();
    this._setupEventListeners();
  }
  
  // Observed attributes for reactivity
  static get observedAttributes() {
    return ['position', 'width', 'show-overlay', 'lock-body-scroll', 'no-body-padding', 'bg-color', 'open'];
  }
  
  // Lifecycle: when attributes change
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'position':
        this.position = newValue || 'right';
        this._updateStyles();
        break;
      case 'width':
        this.drawerWidth = newValue || '25rem';
        this._updateStyles();
        break;
      case 'show-overlay':
        this.showOverlay = newValue !== 'false';
        this._updateStyles();
        break;
      case 'lock-body-scroll':
        this.lockBodyScroll = newValue !== 'false';
        break;
      case 'no-body-padding':
        this.noBodyPadding = this.hasAttribute('no-body-padding');
        this._updateStyles();
        break;
      case 'bg-color':
        this.backgroundColor = newValue || '#fff';
        this._updateStyles();
        break;
      case 'open':
        if (newValue === null) {
          this.close();
        } else {
          this.open();
        }
        break;
    }
  }
  
  // Lifecycle: when component is connected to the DOM
  connectedCallback() {
    // Check if it should be open by default
    if (this.hasAttribute('open')) {
      this.open();
    }
  }
  
  // Lifecycle: when component is disconnected from the DOM
  // disconnectedCallback() {
  //   // Clean up any event listeners or references
  //   if (this.closeBtn) {
  //     this.closeBtn.removeEventListener('click', this._handleClose);
  //   }
  //   if (this.overlay) {
  //     this.overlay.removeEventListener('click', this._handleClose);
  //   }
  // }
  
  // Create the drawer layout
  _createDrawer() {
    // Create the drawer elements
    const container = document.createElement('div');
    container.className = 'drawer-container';
    
    const content = document.createElement('div');
    content.className = 'drawer-content';
    content.setAttribute('role', 'dialog');
    content.setAttribute('data-lenis-prevent', '');
    
    // Create header section with slot
    const headerSection = document.createElement('div');
    headerSection.className = 'drawer-header';
    const headerSlot = document.createElement('slot');
    headerSlot.name = 'header';
    headerSection.appendChild(headerSlot);
    
    // Create body section with slot
    const bodySection = document.createElement('div');
    bodySection.className = 'drawer-body';
    bodySection.setAttribute('data-lenis-prevent', '');
    const bodySlot = document.createElement('slot');
    bodySection.appendChild(bodySlot);
    
    // Create footer section with slot
    const footerSection = document.createElement('div');
    footerSection.className = 'drawer-footer';
    const footerSlot = document.createElement('slot');
    footerSlot.name = 'footer';
    footerSection.appendChild(footerSlot);
    
    // Create default close button
    this.closeBtn = document.createElement('button');
    this.closeBtn.className = 'drawer-close-btn';
    this.closeBtn.innerHTML = '&#10006;';
    this.closeBtn.setAttribute('aria-label', 'Close');
    headerSection.appendChild(this.closeBtn);
    
    content.appendChild(headerSection);
    content.appendChild(bodySection);
    content.appendChild(footerSection);
    
    // Create overlay if needed
    if (this.showOverlay) {
      this.overlay = document.createElement('div');
      this.overlay.className = 'drawer-overlay';
      this.overlay.setAttribute('role', 'presentation');
      container.appendChild(this.overlay);
    }
    
    container.appendChild(content);
    
    // Create and attach the styles
    const style = document.createElement('style');
    style.textContent = this._getStyles();
    
    // Append everything to the shadow DOM
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(container);
    
    // Store references
    this.container = container;
    this.contentElement = content;
  }
  
  // Set up event listeners
  _setupEventListeners() {
    // Close button event
    this._handleClose = () => this.close();
    
    // Add event listener to the default close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this._handleClose);
    }
    
    // When the overlay is clicked
    if (this.overlay) {
      this.overlay.addEventListener('click', this._handleClose);
    }
    
    // Prevent clicks inside drawer from closing it
    this.contentElement.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }
  
  // Update styles when props change
  _updateStyles() {
    const styleElement = this.shadowRoot.querySelector('style');
    styleElement.textContent = this._getStyles();
  }
  
  // Generate the styles based on props
  _getStyles() {
    return `
      :host {
        display: block;
        visibility: hidden;
        position: fixed;
        z-index: 9999;
        inset: 0;
        pointer-events: none;
        transition: visibility 0.3s;
      }
      
      :host([open]) {
        visibility: visible;
        pointer-events: all;
      }
      
      .drawer-container {
        position: absolute;
        inset: 0;
        display: flex;
        justify-content: ${this.position === 'left' ? 'flex-start' : 'flex-end'};
        align-items: ${this.position === 'top' ? 'flex-start' : this.position === 'bottom' ? 'flex-end' : 'stretch'};
      }
      
      .drawer-overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.3s;
      }
      
      :host([open]) .drawer-overlay {
        opacity: 1;
      }
      
      .drawer-content {
        position: relative;
        z-index: 2;
        background-color: ${this.backgroundColor};
        width: ${this.position === 'top' || this.position === 'bottom' ? '100%' : this.drawerWidth};
        height: ${this.position === 'left' || this.position === 'right' ? '100%' : 'auto'};
        max-height: ${this.position === 'top' || this.position === 'bottom' ? '80vh' : '100%'};
        display: grid;
        grid-template-rows: auto 1fr auto;
        overflow-y: auto;
        transform: ${
          this.position === 'right' ? 'translateX(100%)' :
          this.position === 'left' ? 'translateX(-100%)' :
          this.position === 'top' ? 'translateY(-100%)' :
          'translateY(100%)'
        };
        transition: transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      }
      
      :host([open]) .drawer-content {
        transform: translate(0);
      }
      
      .drawer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-bottom: 1px solid #ffffff50;
      }
      
      .drawer-close-btn {
        background: none;
        border: none;
        font-size: 1em;
        line-height: 1;
        cursor: pointer;
        padding: 0.5rem;
        transition: transform 0.3s, color 0.3s;
      }

      .drawer-close-btn:hover {
        transform: scale(1.2) rotate(90deg);
        transform-origin: center;
        color: red;
      }
      
      .drawer-body {
        overflow-y: auto;
        padding: ${this.noBodyPadding ? '0' : '1rem'};
      }

      .drawer-footer {
        padding: 0.8rem 1rem;
        border-top: 1px solid #ffffff50;
      }

    `;
  }
  
  // Public methods
  
  /**
   * Open the drawer
   */
  open() {
    this.setAttribute('open', '');
    this.contentElement.setAttribute('aria-hidden', 'false');

    if (window.scrollLock && window.scrollLock.disable) {
      window.scrollLock.disable();
    }
    
    // Dispatch open event
    this.dispatchEvent(new CustomEvent('drawer-opened', {
      bubbles: true,
      composed: true
    }));
  }
  
  /**
   * Close the drawer
   */
  close() {
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    this.removeAttribute('open');
    this.contentElement.setAttribute('aria-hidden', 'true');
    
    if (window.scrollLock && window.scrollLock.enable) {
      window.scrollLock.enable();
    }
    
    // Dispatch close event
    this.dispatchEvent(new CustomEvent('drawer-closed', {
      bubbles: true,
      composed: true
    }));
  }
  
  /**
   * Toggle the drawer open/closed state
   */
  toggle() {
    if (this.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }
}

// Register the custom element
if (!customElements.get('sidebar-drawer')) {
  customElements.define('sidebar-drawer', SidebarDrawer);
}










// Cart Count
// ---------------
// Example Usage:
// <cart-count data-cart-count="{{ cart.item_count }}"></cart-count>

  class CartCount extends HTMLElement {
    constructor() {
      super();

      // Attach shadow DOM
      const shadow = this.attachShadow({ mode: 'open' });

      // Clone the template and append it to shadow DOM
      const template = document.createElement('template');
      template.innerHTML = `
      <span class="cart__items-count" id="cartCount">0</span>
      `
      const clone = template.content.cloneNode(true);
      shadow.appendChild(clone);

      // Bind event handler for cart update
      this.updateCartCount = this.updateCartCount.bind(this);
    }

    // Set the initial cart count when the element is added to the DOM
    connectedCallback() {
      const cartCount = this.getAttribute('data-cart-count') || 0;
      this.shadowRoot.getElementById('cartCount').textContent = cartCount;

      // Listen for cart updates
      window.addEventListener('cart-updated', this.updateCartCount);
    }

    // Update the cart count when the event is fired
    updateCartCount(event) {
      const newCount = event.detail.cartCount || 0;
      this.shadowRoot.getElementById('cartCount').textContent = newCount;
    }

    // Clean up event listener when component is removed
    disconnectedCallback() {
      window.removeEventListener('cart-updated', this.updateCartCount);
    }
  }

  // Define the custom element
  customElements.define('cart-count', CartCount);


  // Function to trigger a cart update (example for adding/removing items)
  function updateCartItemCount(newCount) {
    // Dispatch the custom event with the updated count
    const event = new CustomEvent('cart-updated', {
      detail: {
        cartCount: newCount
      },
      bubbles: true,
      composed: true
    });

    // Dispatch the event on the window object to notify all cart-count components
    window.dispatchEvent(event);
  }


  // Create a namespace for all cart-related utilities
  window.CartUtils = window.CartUtils || {};
  window.CartUtils.updateCartItemCount = updateCartItemCount;











  // Recommended products

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        const isError = html.querySelector('.msg-on-empty-recommendations');
        if (isError) {
          this.parentElement.remove();
        }

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

      })
      .catch((e) => {
        console.error(e);
        this.querySelector(".msg-on-empty-recommendations").textContent = "No related products found";
        this.querySelector(".loader")?.remove()
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}